// Pure helpers pulled out of HelpPanel.tsx so the two riskiest decisions —
// which blocks a support_config response should render, and which clipboard
// path a copy click should take — are unit-testable without a DOM.

export type SupportConfigLike = {
  business_email?: string | null;
  telegram_channel_url?: string | null;
};

export type SupportBlockVisibility = {
  telegram: boolean;
  business: boolean;
};

/**
 * A blank config value is a deliberate product state (ops has not filled in
 * this country's Telegram link or business inbox yet), not missing data —
 * so the block must not render at all: no card with a blank line, no
 * `mailto:` to nowhere. Whitespace-only values count as blank too, and a
 * config that has not loaded yet (undefined) hides both blocks rather than
 * flashing them before they disappear.
 */
export function supportBlockVisibility(config: SupportConfigLike | null | undefined): SupportBlockVisibility {
  return {
    telegram: !!(config?.telegram_channel_url ?? '').trim(),
    business: !!(config?.business_email ?? '').trim(),
  };
}

export type ClipboardCopyResult = 'clipboard' | 'fallback';

/**
 * navigator.clipboard is unavailable on non-HTTPS origins and in older
 * in-app WebViews — exactly where this app runs — so a copy button that
 * only calls it silently does nothing there. This isolates the *selection*
 * logic (try the async API when it exists, fall back to the caller's
 * execCommand implementation on any failure — missing API, permission
 * denial, unsupported context) so it can be tested with fakes instead of a
 * real clipboard or a hidden textarea.
 */
export async function copyWithFallback(
  text: string,
  deps: {
    clipboardWrite?: ((text: string) => Promise<void>) | null;
    fallbackCopy: (text: string) => void;
  },
): Promise<ClipboardCopyResult> {
  if (deps.clipboardWrite) {
    try {
      await deps.clipboardWrite(text);
      return 'clipboard';
    } catch {
      // Falls through to the execCommand fallback below.
    }
  }
  deps.fallbackCopy(text);
  return 'fallback';
}
